import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Modal,
  TextField,
  Spinner,
  Box,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  ActionList,
  Button,
  Thumbnail,
  Icon,
  Tag,
  Popover,
} from '@/lib/polaris';
import {
  ProductIcon,
  OrderIcon,
  PersonAddIcon,
  HomeIcon,
  ChatIcon,
  SendIcon,
  SettingsIcon,
  StarFilledIcon,
  ChartVerticalFilledIcon,
  PersonIcon,
  SearchIcon,
  FilterIcon,
} from '@/lib/polaris-icons';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { ROUTES } from '@/config/routes.config';
import { logError } from '@/lib/logger';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Buscador global del admin (⌘K / Ctrl+K) — superficie ÚNICA estilo Shopify.
 *
 * Unifica en un solo Modal Polaris:
 *   • Entidades: productos, pedidos, clientes (chips de alcance removibles).
 *   • Navegación: saltar a cualquier sección del panel.
 *   • Acciones: comandos globales (nueva campaña, abrir marcador…).
 *   • Conversaciones: búsqueda en vivo de chats de WhatsApp por nombre/teléfono.
 *
 * Antes esto vivía repartido en 3 componentes (AdminGlobalSearch + 2
 * CommandPalette, uno con cmdk/radix no-Polaris). Consolidado 2026-07-06.
 * Estado vacío = lanzador (navegación + acciones), como el spotlight de Shopify.
 */

type IconSource = React.FunctionComponent<React.SVGProps<SVGSVGElement>>;

interface SearchResult {
  id: string;
  type: 'product' | 'order' | 'customer';
  title: string;
  subtitle?: string;
  route: string;
  price?: number;
  total?: number;
  isActive?: boolean;
  status?: string;
  paymentStatus?: string;
  customerName?: string;
  createdAt?: string;
  email?: string;
  imageUrl?: string | null;
  itemCount?: number;
}

/** ¿Hay más resultados que el límite mostrado? (para "Mostrar 20 más"). */
interface HasMore {
  product: boolean;
  order: boolean;
  customer: boolean;
}

/** Ítem de navegación o acción (el "lanzador" del command palette). */
interface LauncherItem {
  id: string;
  label: string;
  hint?: string;
  icon: IconSource;
  kind: 'nav' | 'action';
  /** Se muestra en el estado vacío (accesos rápidos). */
  quick?: boolean;
  run: () => void;
}

/** Conversación de WhatsApp (resultado en vivo). */
interface ConversationResult {
  id: string;
  label: string;
  hint?: string;
  run: () => void;
}

type FilterType = 'all' | 'product' | 'order' | 'customer';

const FILTERS: { id: FilterType; label: string }[] = [
  { id: 'all', label: 'Todo' },
  { id: 'product', label: 'Productos' },
  { id: 'order', label: 'Pedidos' },
  { id: 'customer', label: 'Clientes' },
];

type EntityFilter = 'product' | 'order' | 'customer';

/** Footer "Ver todos {…}" por scope → ruta de la lista completa. */
const SCOPE_ROUTE: Record<EntityFilter, string> = {
  product: ROUTES.PRODUCTS,
  order: ROUTES.ORDERS,
  customer: ROUTES.CUSTOMERS,
};
const SCOPE_ALL_LABEL: Record<EntityFilter, string> = {
  product: 'los productos',
  order: 'los pedidos',
  customer: 'los clientes',
};

function getIcon(type: SearchResult['type']): IconSource {
  switch (type) {
    case 'product':
      return ProductIcon;
    case 'order':
      return OrderIcon;
    case 'customer':
      return PersonAddIcon;
    default:
      return ProductIcon;
  }
}

function getTypeLabel(type: string) {
  switch (type) {
    case 'product':
      return 'Productos';
    case 'order':
      return 'Pedidos';
    case 'customer':
      return 'Clientes';
    default:
      return '';
  }
}

function getStatusLabel(status: string) {
  const map: Record<string, string> = {
    pending: 'No preparado',
    preparing: 'Preparando',
    ready: 'Listo',
    delivered: 'Entregado',
    cancelled: 'Cancelado',
  };
  return map[status] || status;
}

/** Fuzzy match estilo Sublime/VSCode: letras de `query` en orden dentro de `text`. */
function fuzzyMatch(text: string, query: string): boolean {
  if (!query) return true;
  const t = text.toLowerCase();
  const q = query.toLowerCase();
  let j = 0;
  for (let i = 0; i < t.length && j < q.length; i++) {
    if (t[i] === q[j]) j++;
  }
  return j === q.length;
}

// ─── Mappers row → SearchResult (a nivel de módulo: sin dependencias de estado) ──
function toProductResult(p: Record<string, unknown>): SearchResult {
  return {
    id: p.id as string,
    type: 'product',
    title: p.name as string,
    subtitle: (p.category as string) || '',
    route: ROUTES.PRODUCTS,
    price: p.price as number,
    isActive: p.is_active as boolean,
    imageUrl: typeof p.image_url === 'string' && (p.image_url.startsWith('http') || p.image_url.startsWith('/')) ? p.image_url : null,
  };
}

function toOrderResult(o: Record<string, unknown>): SearchResult {
  const id = o.id as string;
  const lines = (o.order_lines as { qty?: number }[] | null) ?? [];
  const itemCount = lines.reduce((sum, l) => sum + (Number(l.qty) || 0), 0);
  return {
    id,
    type: 'order',
    title: `#${(o.order_number as string) || id.slice(0, 8)}`,
    subtitle: (o.customer_name as string) || 'Sin cliente',
    route: ROUTES.ORDERS,
    total: o.total as number,
    status: o.status as string,
    paymentStatus: o.payment_status as string,
    customerName: o.customer_name as string,
    createdAt: o.created_at as string,
    itemCount,
  };
}

function toCustomerResult(c: Record<string, unknown>): SearchResult {
  const name = (c.customer_name as string) ?? '';
  return {
    id: `customer-${name}`,
    type: 'customer',
    title: name,
    subtitle: (c.customer_email as string) || 'Sin email',
    route: ROUTES.CUSTOMERS,
    email: (c.customer_email as string) ?? undefined,
    customerName: name,
  };
}

const BODY_SEARCH_OPEN_CLASS = 'sf-search-modal-open';

export function AdminGlobalSearch({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { tenantId } = useAuth();
  const storeNavigate = useStoreNavigate();

  useEffect(() => {
    if (open) {
      document.body.classList.add(BODY_SEARCH_OPEN_CLASS);
    } else {
      document.body.classList.remove(BODY_SEARCH_OPEN_CLASS);
    }
    return () => document.body.classList.remove(BODY_SEARCH_OPEN_CLASS);
  }, [open]);

  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [convItems, setConvItems] = useState<ConversationResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [hasMore, setHasMore] = useState<HasMore>({ product: false, order: false, customer: false });
  const [showMore, setShowMore] = useState<Record<string, number>>({});

  // Cancelación de la búsqueda en vuelo + descarte de respuestas viejas (evita
  // races y spinner pegado). El catálogo cacheado se carga UNA vez por apertura
  // (no en cada tecla) y se filtra en memoria = typeahead instantáneo.
  const [tenantName, setTenantName] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const seqRef = useRef(0);
  const cachedProductsRef = useRef<Record<string, unknown>[] | null>(null);

  // Al abrir, invalidamos el cache local para recargarlo (por si cambió de tenant).
  useEffect(() => {
    if (open) cachedProductsRef.current = null;
  }, [open]);

  // Nombre de la tienda para el estado vacío ("Busca cualquier cosa en {tienda}").
  useEffect(() => {
    if (!open || !tenantId || tenantName) return;
    let active = true;
    supabase.from('tenants').select('name').eq('id', tenantId).maybeSingle().then(({ data }) => {
      const name = (data as { name?: string } | null)?.name;
      if (active && name) setTenantName(name);
    });
    return () => { active = false; };
  }, [open, tenantId, tenantName]);

  const ensureCachedProducts = useCallback(async (): Promise<Record<string, unknown>[]> => {
    if (cachedProductsRef.current) return cachedProductsRef.current;
    try {
      const { getCachedProductsIfFresh } = await import('@/lib/db');
      cachedProductsRef.current = await getCachedProductsIfFresh();
    } catch {
      cachedProductsRef.current = [];
    }
    return cachedProductsRef.current;
  }, []);

  const close = useCallback(() => {
    onOpenChange(false);
    setSearchQuery('');
    setActiveFilter('all');
    setShowMore({});
  }, [onOpenChange]);

  // ─── Navegación + Acciones (lanzador, ex-CommandPalette) ──────────────────
  const launcherItems = useMemo<LauncherItem[]>(() => {
    const go = (route: string) => () => {
      close();
      storeNavigate(route);
    };
    return [
      // Navegación — accesos rápidos (quick) primero
      { id: 'nav-home', label: 'Inicio', icon: HomeIcon, kind: 'nav', quick: true, run: go(ROUTES.HOME) },
      { id: 'nav-orders', label: 'Pedidos', hint: 'órdenes', icon: OrderIcon, kind: 'nav', quick: true, run: go(ROUTES.ORDERS) },
      { id: 'nav-products', label: 'Productos', hint: 'catálogo', icon: ProductIcon, kind: 'nav', quick: true, run: go(ROUTES.PRODUCTS) },
      { id: 'nav-customers', label: 'Clientes', icon: PersonIcon, kind: 'nav', quick: true, run: go(ROUTES.CUSTOMERS) },
      { id: 'nav-inbox', label: 'WhatsApp — Inbox', hint: 'conversaciones', icon: ChatIcon, kind: 'nav', quick: true, run: go(ROUTES.WHATSAPP_INBOX) },
      { id: 'nav-settings', label: 'Ajustes', hint: 'configuración', icon: SettingsIcon, kind: 'nav', quick: true, run: go(ROUTES.SETTINGS) },
      { id: 'nav-campaigns', label: 'WhatsApp — Campañas', hint: 'broadcast', icon: SendIcon, kind: 'nav', run: go(ROUTES.WHATSAPP_CAMPAIGNS) },
      { id: 'nav-csat', label: 'WhatsApp — CSAT', hint: 'satisfacción', icon: StarFilledIcon, kind: 'nav', run: go(ROUTES.WHATSAPP_CSAT) },
      { id: 'nav-skills', label: 'WhatsApp — Skills y enrutamiento', hint: 'skills routing', icon: SettingsIcon, kind: 'nav', run: go(ROUTES.WHATSAPP_SKILLS) },
      { id: 'nav-supervisor', label: 'WhatsApp — Supervisor', hint: 'dashboard', icon: ChartVerticalFilledIcon, kind: 'nav', run: go(ROUTES.WHATSAPP_SUPERVISOR) },
      { id: 'nav-stats', label: 'WhatsApp — Informes', hint: 'stats', icon: ChartVerticalFilledIcon, kind: 'nav', run: go(ROUTES.WHATSAPP_STATS) },
      { id: 'nav-contacts', label: 'WhatsApp — Contactos', icon: PersonIcon, kind: 'nav', run: go(ROUTES.WHATSAPP_CONTACTS) },
      { id: 'nav-macros', label: 'WhatsApp — Macros', icon: SettingsIcon, kind: 'nav', run: go(ROUTES.WHATSAPP_MACROS) },
      { id: 'nav-tags', label: 'WhatsApp — Etiquetas', icon: SettingsIcon, kind: 'nav', run: go(ROUTES.WHATSAPP_TAGS) },
      // Acciones
      { id: 'act-new-campaign', label: 'Nueva campaña de WhatsApp', hint: 'broadcast', icon: SendIcon, kind: 'action', quick: true, run: go(ROUTES.WHATSAPP_CAMPAIGNS) },
      { id: 'act-call', label: 'Abrir marcador (call center)', hint: 'llamar', icon: ChatIcon, kind: 'action', quick: true, run: () => {
        close();
        window.dispatchEvent(new CustomEvent('sf:open-dialer'));
      } },
    ];
  }, [storeNavigate, close]);

  const performSearch = useCallback(async () => {
    const query = searchQuery.trim().toLowerCase();
    const hasQuery = query.length > 0;

    // Cancelamos la búsqueda anterior (requests colgados + races).
    abortRef.current?.abort();

    // 'Todo' sin texto = lanzador (nav/acciones); no listamos entidades. Con un
    // scope de entidad activo SÍ mostramos recientes aunque no haya texto.
    if (!tenantId || (!hasQuery && activeFilter === 'all')) {
      setResults([]);
      setHasMore({ product: false, order: false, customer: false });
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const signal = controller.signal;
    const seq = ++seqRef.current;
    const isStale = () => seq !== seqRef.current;

    setLoading(true);

    const wantProduct = activeFilter === 'all' || activeFilter === 'product';
    const wantOrder = activeFilter === 'all' || activeFilter === 'order';
    const wantCustomer = activeFilter === 'all' || activeFilter === 'customer';

    // Cada fetch atrapa sus propios errores y devuelve vacío: nunca rechaza, así
    // Promise.all no aborta a los hermanos y el guard de `seq` descarta lo viejo.
    // `hasMore`: pedimos limit+1 filas para saber si hay más SIN un COUNT extra.
    const fetchProducts = async (): Promise<{ results: SearchResult[]; hasMore: boolean }> => {
      if (!wantProduct) return { results: [], hasMore: false };
      const limit = activeFilter === 'product' ? (showMore.product || 10) : 15;
      try {
        // Con texto: cache local (cargado 1 vez por apertura) filtrado en memoria = instantáneo.
        if (hasQuery) {
          const cached = await ensureCachedProducts();
          if (cached.length > 0) {
            const filtered = cached.filter((p) => (p.name as string)?.toLowerCase().includes(query));
            return { results: filtered.slice(0, limit).map(toProductResult), hasMore: filtered.length > limit };
          }
        }
        let q = supabase
          .from('products')
          .select('id, name, price, category, is_active, image_url')
          .eq('tenant_id', tenantId);
        q = hasQuery ? q.ilike('name', `%${query}%`) : q.order('name', { ascending: true });
        const { data } = await q.limit(limit + 1).abortSignal(signal);
        const rows = data ?? [];
        return { results: rows.slice(0, limit).map(toProductResult), hasMore: rows.length > limit };
      } catch {
        return { results: [], hasMore: false };
      }
    };

    const fetchOrders = async (): Promise<{ results: SearchResult[]; hasMore: boolean }> => {
      if (!wantOrder) return { results: [], hasMore: false };
      const limit = activeFilter === 'order' ? (showMore.order || 10) : 15;
      try {
        // orders_v2 = dominio único (todos los canales). Con texto matchea por
        // cliente O número de pedido; sin texto = pedidos recientes.
        let q = supabase
          .from('orders_v2')
          // order_lines(qty) = embedding PostgREST: trae las qty por pedido en la
          // MISMA query (sin N+1) para el conteo "· N artículos".
          .select('id, order_number, customer_name, total:grand_total, status:lifecycle_stage, payment_status, created_at, order_lines(qty)')
          .eq('tenant_id', tenantId)
          .not('customer_name', 'is', null);
        if (hasQuery) q = q.or(`customer_name.ilike.%${query}%,order_number.ilike.%${query}%`);
        const { data, error } = await q.order('created_at', { ascending: false }).limit(limit + 1).abortSignal(signal);
        if (error) {
          if (!signal.aborted) logError('Error buscando pedidos', error);
          return { results: [], hasMore: false };
        }
        const rows = data ?? [];
        return { results: rows.slice(0, limit).map(toOrderResult), hasMore: rows.length > limit };
      } catch {
        return { results: [], hasMore: false };
      }
    };

    const fetchCustomers = async (): Promise<{ results: SearchResult[]; hasMore: boolean }> => {
      if (!wantCustomer) return { results: [], hasMore: false };
      const limit = activeFilter === 'customer' ? (showMore.customer || 10) : 15;
      try {
        // Un mismo cliente repite en varios pedidos → pedimos de sobra y deduplicamos.
        let q = supabase
          .from('orders_v2')
          .select('customer_name, customer_email, created_at')
          .eq('tenant_id', tenantId)
          .not('customer_name', 'is', null);
        if (hasQuery) q = q.ilike('customer_name', `%${query}%`);
        const { data, error } = await q.order('created_at', { ascending: false }).limit((limit + 1) * 4).abortSignal(signal);
        if (error) {
          if (!signal.aborted) logError('Error buscando clientes', error);
          return { results: [], hasMore: false };
        }
        const uniq = new Map<string, Record<string, unknown>>();
        (data ?? []).forEach((c) => {
          const name = (c.customer_name as string | null)?.toLowerCase();
          if (name && !uniq.has(name)) uniq.set(name, c);
        });
        const unique = Array.from(uniq.values());
        return { results: unique.slice(0, limit).map(toCustomerResult), hasMore: unique.length > limit };
      } catch {
        return { results: [], hasMore: false };
      }
    };

    // Las 3 en PARALELO; aplicamos resultados UNA sola vez al final (sin parpadeo).
    const [prod, ord, cust] = await Promise.all([fetchProducts(), fetchOrders(), fetchCustomers()]);
    if (isStale()) return; // llegó una búsqueda más nueva → descartar esta

    setResults([...prod.results, ...ord.results, ...cust.results]);
    setHasMore({ product: prod.hasMore, order: ord.hasMore, customer: cust.hasMore });
    setLoading(false);
  }, [searchQuery, activeFilter, tenantId, showMore, ensureCachedProducts]);

  useEffect(() => {
    // Debounce 200ms: agrupa tecleo rápido en una sola búsqueda.
    const t = setTimeout(performSearch, 200);
    return () => clearTimeout(t);
  }, [performSearch]);

  // ─── Conversaciones de WhatsApp en vivo (solo alcance "Todo", ≥2 chars) ───
  useEffect(() => {
    const q = searchQuery.trim();
    if (!open || !tenantId || activeFilter !== 'all' || q.length < 2) {
      setConvItems([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const { data, error } = await supabase
        .from('wa_conversations')
        .select('id, contact_phone, contact_name, last_message_at')
        .eq('tenant_id', tenantId)
        .or(`contact_phone.ilike.%${q}%,contact_name.ilike.%${q}%`)
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .limit(6)
        .abortSignal(controller.signal);
      if (error) return;
      const items: ConversationResult[] = ((data as Array<Record<string, unknown>> | null) ?? []).map((c) => ({
        id: `conv-${c.id}`,
        label: (c.contact_name as string) || (c.contact_phone as string) || 'Conversación',
        hint: (c.contact_name as string) ? (c.contact_phone as string) : undefined,
        run: () => {
          close();
          storeNavigate(`${ROUTES.WHATSAPP_INBOX}?conversation=${c.id}`);
        },
      }));
      setConvItems(items);
    }, 160);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, searchQuery, activeFilter, tenantId, storeNavigate, close]);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      close();
      if (result.type === 'product') {
        storeNavigate(`/products/products/${result.id}/edit`);
      } else if (result.type === 'order') {
        storeNavigate(`${ROUTES.ORDERS}?id=${result.id}`);
      } else if (result.type === 'customer') {
        const name = result.customerName || result.title;
        storeNavigate(`${ROUTES.CUSTOMERS}?name=${encodeURIComponent(name)}`);
      } else {
        storeNavigate(result.route);
      }
    },
    [storeNavigate, close]
  );

  const grouped = useMemo(() => {
    const product = results.filter((r) => r.type === 'product');
    const order = results.filter((r) => r.type === 'order');
    const customer = results.filter((r) => r.type === 'customer');
    if (activeFilter === 'all') return { product, order, customer };
    return {
      product: activeFilter === 'product' ? product : [],
      order: activeFilter === 'order' ? order : [],
      customer: activeFilter === 'customer' ? customer : [],
    };
  }, [results, activeFilter]);

  // Navegación / Acciones que matchean la query (solo alcance "Todo").
  const matchingLauncher = useMemo(() => {
    if (activeFilter !== 'all') return { nav: [], action: [] };
    const nav = launcherItems.filter((i) => i.kind === 'nav' && fuzzyMatch(`${i.label} ${i.hint ?? ''}`, searchQuery));
    const action = launcherItems.filter((i) => i.kind === 'action' && fuzzyMatch(`${i.label} ${i.hint ?? ''}`, searchQuery));
    return { nav, action };
  }, [launcherItems, searchQuery, activeFilter]);

  const emptyQuery = !searchQuery.trim();
  // El lanzador (nav/acciones) solo aplica en 'Todo' sin texto. En un scope de
  // entidad, aunque no haya texto, mostramos su lista de recientes.
  const showLauncher = emptyQuery && activeFilter === 'all';
  const hasEntityResults = results.length > 0;
  const hasAnyResult =
    hasEntityResults || convItems.length > 0 || matchingLauncher.nav.length > 0 || matchingLauncher.action.length > 0;
  const noResults = !showLauncher && !loading && !hasAnyResult;

  const launcherSection = (title: string, items: LauncherItem[]) =>
    items.length > 0
      ? [{
          title,
          items: items.map((it) => ({
            content: it.label,
            helpText: it.hint || undefined,
            icon: it.icon,
            onAction: it.run,
          })),
        }]
      : [];

  // Navegación por teclado tipo spotlight: ↑/↓ mueven el foco entre resultados
  // (buttons role=menuitem), Enter activa. Desde el input, ↓ baja al primer
  // resultado y Enter dispara el primero. El foco nativo hace de "selección".
  const handleKeyNav = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
    const container = e.currentTarget;
    const items = Array.from(container.querySelectorAll<HTMLElement>('[role="menuitem"]'));
    if (items.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    const idx = active ? items.indexOf(active) : -1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      (idx < items.length - 1 ? items[idx + 1] : items[0]).focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx <= 0) container.querySelector<HTMLInputElement>('input')?.focus();
      else items[idx - 1].focus();
    } else if (e.key === 'Enter' && idx === -1) {
      // Foco en el input → Enter dispara el primer resultado.
      e.preventDefault();
      items[0]?.click();
    }
  }, []);

  return (
    <Modal open={open} onClose={close} title="Buscar" titleHidden>
      <Modal.Section>
        <div onKeyDown={handleKeyNav}>
        <BlockStack gap="400">
          <TextField
            label="Buscar productos, pedidos, clientes, secciones y acciones"
            labelHidden
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Buscar"
            autoComplete="off"
            autoFocus
            clearButton
            onClearButtonClick={() => setSearchQuery('')}
            prefix={
              <InlineStack gap="150" blockAlign="center" wrap={false}>
                <Icon source={SearchIcon} tone="subdued" />
                {/* Scope activo = token removible DENTRO del input (estilo Shopify). */}
                {activeFilter !== 'all' && (
                  <Tag onRemove={() => { setActiveFilter('all'); setShowMore({}); }}>
                    {FILTERS.find((f) => f.id === activeFilter)?.label ?? ''}
                  </Tag>
                )}
              </InlineStack>
            }
            suffix={
              /* Icono de filtro (≡) estilo Shopify: abre la lista "Filtrar por". */
              <Popover
                active={filterOpen}
                onClose={() => setFilterOpen(false)}
                preferredAlignment="right"
                activator={
                  <Button
                    variant="tertiary"
                    icon={FilterIcon}
                    accessibilityLabel="Filtrar por"
                    onClick={() => setFilterOpen((v) => !v)}
                  />
                }
              >
                <ActionList
                  actionRole="menuitem"
                  sections={[{
                    title: 'Filtrar por',
                    items: FILTERS.filter((f) => f.id !== 'all').map((f) => ({
                      content: f.label,
                      active: activeFilter === f.id,
                      onAction: () => {
                        setActiveFilter(f.id);
                        setShowMore({});
                        setFilterOpen(false);
                      },
                    })),
                  }]}
                />
              </Popover>
            }
          />
          {/* Fila de scopes para SELECCIONAR: pills plomo nativos de Polaris
              (Tag clickeable), como Shopify. Al elegir uno, se mueve al input
              como token y esta fila se oculta. */}
          {activeFilter === 'all' && (
            <InlineStack gap="200" wrap>
              {FILTERS.filter((f) => f.id !== 'all').map((f) => (
                <Tag
                  key={f.id}
                  onClick={() => {
                    setActiveFilter(f.id);
                    setShowMore({});
                  }}
                >
                  {f.label}
                </Tag>
              ))}
            </InlineStack>
          )}
          <div className="sf-search-body" data-expanded={String(!showLauncher)}>
            {/* 'Todo' sin texto = empty state compacto estilo Shopify (lupa + hint). */}
            {showLauncher && (
              <div className="sf-search-empty">
                <span className="sf-search-empty__icon" aria-hidden="true"><SearchIcon /></span>
                <Text as="p" variant="bodyMd" tone="subdued">
                  Busca cualquier cosa en {tenantName || 'tu tienda'}
                </Text>
              </div>
            )}
            {/* Spinner solo en la PRIMERA búsqueda (sin resultados aún). En
                recargas (tipear más / "Mostrar 20 más") mantenemos las filas
                visibles y solo se actualizan al llegar — sin parpadeo, como Shopify. */}
            {!showLauncher && loading && !hasAnyResult && (
              <Box padding="800">
                <InlineStack align="center" blockAlign="center" gap="300">
                  <Spinner accessibilityLabel="Buscando" size="large" />
                  <Text as="span" tone="subdued">Buscando...</Text>
                </InlineStack>
              </Box>
            )}
            {noResults && (
              <Box padding="800">
                <Text as="p" variant="bodyMd" tone="subdued">
                  {emptyQuery
                    ? 'No hay resultados para mostrar todavía.'
                    : `No se encontraron resultados para "${searchQuery}"`}
                </Text>
              </Box>
            )}
            {!showLauncher && hasAnyResult && (
              <BlockStack gap="400">
                <ActionList
                  actionRole="menuitem"
                  sections={[
                    // Acciones que matchean (solo alcance "Todo").
                    ...launcherSection('Acciones', matchingLauncher.action),
                    ...((activeFilter === 'all' || activeFilter === 'product') && grouped.product.length > 0
                      ? [{
                          title: getTypeLabel('product'),
                          items: grouped.product.map((r) => ({
                            content: r.title,
                            helpText: r.price != null ? `${r.subtitle ? `${r.subtitle} · ` : ''}S/ ${Number(r.price).toFixed(2)}` : r.subtitle || undefined,
                            suffix: r.isActive !== undefined ? <Badge tone={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Activo' : 'Inactivo'}</Badge> : undefined,
                            prefix: r.imageUrl ? (
                              <Thumbnail source={r.imageUrl} alt={r.title} size="small" />
                            ) : (
                              <Icon source={ProductIcon} tone="subdued" />
                            ),
                            onAction: () => handleSelect(r),
                          })),
                        }]
                      : []),
                    ...((activeFilter === 'all' || activeFilter === 'order') && grouped.order.length > 0
                      ? [{
                          title: getTypeLabel('order'),
                          items: grouped.order.map((r) => ({
                            content: r.title,
                            helpText: r.createdAt
                              ? `${r.customerName} · ${format(new Date(r.createdAt), 'dd MMM, HH:mm', { locale: es })} · S/ ${r.total?.toFixed(2)}${r.itemCount ? ` · ${r.itemCount} artículo${r.itemCount === 1 ? '' : 's'}` : ''}`
                              : undefined,
                            suffix: (r.status || r.paymentStatus) ? (
                              <InlineStack gap="100" blockAlign="center">
                                {r.status && <Badge tone="attention">{getStatusLabel(r.status)}</Badge>}
                                {r.paymentStatus && <Badge tone={r.paymentStatus === 'paid' ? 'success' : 'warning'}>{r.paymentStatus === 'paid' ? 'Pagado' : 'Pendiente'}</Badge>}
                              </InlineStack>
                            ) : undefined,
                            icon: getIcon(r.type),
                            onAction: () => handleSelect(r),
                          })),
                        }]
                      : []),
                    ...((activeFilter === 'all' || activeFilter === 'customer') && grouped.customer.length > 0
                      ? [{
                          title: getTypeLabel('customer'),
                          items: grouped.customer.map((r) => ({
                            content: r.title,
                            helpText: r.email || undefined,
                            icon: getIcon(r.type),
                            onAction: () => handleSelect(r),
                          })),
                        }]
                      : []),
                    // Conversaciones de WhatsApp en vivo (solo alcance "Todo").
                    ...(convItems.length > 0
                      ? [{
                          title: 'Conversaciones',
                          items: convItems.map((c) => ({
                            content: c.label,
                            helpText: c.hint || undefined,
                            icon: ChatIcon,
                            onAction: c.run,
                          })),
                        }]
                      : []),
                    // Navegación que matchea (solo alcance "Todo").
                    ...launcherSection('Ir a', matchingLauncher.nav),
                  ]}
                />
                {activeFilter !== 'all' && hasMore[activeFilter] && (
                  <div>
                    <Button
                      variant="plain"
                      onClick={() => setShowMore((s) => ({ ...s, [activeFilter]: (s[activeFilter] || 10) + 20 }))}
                    >
                      Mostrar 20 más
                    </Button>
                  </div>
                )}
                {/* Footer por scope: ir a la lista completa (estilo Shopify). */}
                {activeFilter !== 'all' && (
                  <Box borderColor="border" borderBlockStartWidth="025" paddingBlockStart="200">
                    <Button
                      variant="plain"
                      icon={getIcon(activeFilter)}
                      onClick={() => {
                        close();
                        storeNavigate(SCOPE_ROUTE[activeFilter]);
                      }}
                    >
                      {`Ver todos ${SCOPE_ALL_LABEL[activeFilter]}`}
                    </Button>
                  </Box>
                )}
              </BlockStack>
            )}
          </div>
        </BlockStack>
        </div>
      </Modal.Section>
    </Modal>
  );
}
